import { themeAtom } from "../../../shared/atoms";
import { ExcelLikeGrid } from "../../../shared/excel-grid/components/ExcelLikeGrid";
import type { ExcelGridColumn, ExcelGridRow } from "../../../shared/excel-grid/models/excel-grid.models";
import type {
  AdminCrewReportVesselCrewMember,
  AdminCrewReportVesselCrewResponse,
  AdminCrewReportViewMetadataProps,
  AdminCrewReportViewModalProps,
} from "../../models/admin-crew-report.models";
import { getAdminCrewReportReport } from "../../services/admin-crew-report.service";
import {
  formatAdminCrewReportDate,
  toAdminCrewReportViewGridRows,
} from "../../utils/admin-crew-report-running-vessels.utils";
import { ADMIN_CREW_REPORT_VIEW_COLUMNS } from "../../utils/admin-crew-report.constants";
import { getAdminCrewReportErrorMessage } from "../../utils/admin-crew-report.utils";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportViewModal = ({ vessel, onClose }: AdminCrewReportViewModalProps) => {
  const theme = useAtomValue(themeAtom);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [response, setResponse] = useState<AdminCrewReportVesselCrewResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const crewMemberByGridRowId = useMemo(
    () =>
      new Map<ExcelGridRow["gridRowId"], AdminCrewReportVesselCrewMember>(
        (response?.rows ?? []).map((row) => [`${vessel.imoNumber}:${row.crewRowNumber}`, row] as const),
      ),
    [response?.rows, vessel.imoNumber],
  );
  const columns = useMemo<ExcelGridColumn[]>(
    () =>
      ADMIN_CREW_REPORT_VIEW_COLUMNS.map((column) => ({
        ...column,
        editable: false,
        filterable: true,
        sortable: true,
        getSortValue:
          column.key === "crewRowNumber"
            ? (row: ExcelGridRow) => Number(row.crewRowNumber)
            : column.key === "signOnDate" || column.key === "changePlannedDate"
              ? (row: ExcelGridRow) => {
                  const crewMember = crewMemberByGridRowId.get(row.gridRowId);
                  return column.key === "signOnDate"
                    ? crewMember?.signOnDate ?? ""
                    : crewMember?.changePlannedDate ?? "";
                }
              : undefined,
        renderCell:
          column.key === "monthsOnBoard"
            ? (row) => (
                <span
                  className={twMerge(
                    "font-normal",
                    row.isLongTenure === true && "font-bold text-[#BF0002] dark:text-red-300",
                  )}
                >
                  {String(row.monthsOnBoard ?? "")}
                </span>
              )
            : column.key === "overdue"
              ? (row) => (
                  <span
                    className={twMerge(
                      "font-normal",
                      row.overdue === "Yes" && "font-bold text-[#BF0002] dark:text-red-300",
                    )}
                  >
                    {String(row.overdue ?? "")}
                  </span>
                )
              : undefined,
      })),
    [crewMemberByGridRowId],
  );
  const rows = useMemo(
    () => toAdminCrewReportViewGridRows(response?.rows ?? [], vessel.imoNumber),
    [response?.rows, vessel.imoNumber],
  );

  useEffect(() => {
    let isCurrentRequest = true;
    setIsLoading(true);
    setErrorMessage("");
    setResponse(null);

    getAdminCrewReportReport(vessel.imoNumber)
      .then((result) => {
        if (isCurrentRequest) setResponse(result);
      })
      .catch((error) => {
        if (isCurrentRequest) setErrorMessage(getAdminCrewReportErrorMessage(error));
      })
      .finally(() => {
        if (isCurrentRequest) setIsLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [vessel.imoNumber]);

  return (
    <Dialog
      as="div"
      open
      initialFocus={titleRef}
      onClose={onClose}
      data-test="admin-crew-report-view-modal"
      className="fixed inset-0 z-50"
    >
      <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          as="section"
          className={twMerge(
            "flex max-h-[92vh] w-full max-w-[min(1480px,96vw)] flex-col bg-white shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <header className="flex flex-none flex-col gap-3 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
            <div className="flex min-w-0 items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase text-green">Stored Vessel Report</p>
                <Dialog.Title
                  as="h2"
                  ref={titleRef}
                  tabIndex={-1}
                  className="mt-0.5 truncate text-lg font-semibold outline-none"
                >
                  Crew Report
                </Dialog.Title>
              </div>
              <button
                type="button"
                aria-label="Close crew report"
                title="Close"
                className="flex h-7 w-7 flex-none items-center justify-center border border-gray-300 text-gray-500 hover:text-green dark:border-gray-700"
                onClick={onClose}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex min-w-0 flex-wrap gap-2 text-xs text-[#4A5655] dark:text-gray-300">
              <ReportMetadata label="Vessel" value={vessel.vesselName || "Not available"} truncate />
              <ReportMetadata label="IMO" value={vessel.imoNumber} />
              <ReportMetadata
                label="Report Date"
                value={isLoading ? "..." : formatAdminCrewReportDate(response?.reportReceivedAt) || "Not available"}
              />
              <ReportMetadata label="Rows" value={isLoading ? "..." : String(response?.rows.length ?? 0)} />
            </div>
          </header>

          <div className="flex min-h-[240px] flex-1 flex-col px-5 py-4">
            {isLoading ? (
              <div
                className="flex min-h-[240px] items-center justify-center text-sm text-[#4A5655] dark:text-gray-300"
                role="status"
              >
                Loading Crew Report.
              </div>
            ) : errorMessage ? (
              <div
                className="flex min-h-[240px] items-center justify-center text-sm font-medium text-[#BF0002]"
                role="alert"
              >
                {errorMessage}
              </div>
            ) : rows.length === 0 ? (
              <div className="flex min-h-[240px] items-center justify-center text-sm text-[#4A5655] dark:text-gray-300">
                No crew rows are stored for this vessel.
              </div>
            ) : (
              <ExcelLikeGrid
                dataTest="admin-crew-report-view-grid"
                rows={rows}
                columns={columns}
                emptyLabel="No crew rows are stored for this vessel."
                showHeadersWhenEmpty
                editingDisabled
                useTabForCellNavigation={false}
                className="max-h-[calc(92vh-250px)] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&_td]:!h-auto [&_td]:!text-center [&_th]:text-center [&_th_button]:justify-center [&_th_button]:text-center [&_th_input]:text-center"
                onCellCommit={() => undefined}
              />
            )}
          </div>

          <footer className="flex flex-none justify-end border-t border-gray-200 px-5 py-3 dark:border-gray-700">
            <button
              type="button"
              className="border border-gray-300 px-5 py-2 text-sm font-semibold hover:border-green hover:text-green dark:border-gray-600"
              onClick={onClose}
            >
              Close
            </button>
          </footer>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

const ReportMetadata = ({ label, value, truncate = false }: AdminCrewReportViewMetadataProps) => (
  <span className="inline-flex max-w-full items-center gap-1 border border-gray-200 bg-[#F8FBFA] px-2 py-1 dark:border-gray-700 dark:bg-gray-900">
    <span className="font-semibold uppercase text-[#637381] dark:text-gray-400">{label}:</span>
    <span className={twMerge("font-medium", truncate && "min-w-0 truncate")}>{value}</span>
  </span>
);

export default AdminCrewReportViewModal;
